import React, { useState } from 'react';
import { useDataSource } from '@/contexts/DataSourceContext';
import type { DataMode, ScreenDataMode } from '@/contexts/DataSourceContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Monitor, 
  Database, 
  TestTube2, 
  AlertCircle, 
  CheckCircle2,
  RotateCcw,
  Info
} from 'lucide-react';
import { PageHeader } from '@/components/blocks/page-header';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface ConfirmationDialog {
  isOpen: boolean;
  screenId: string;
  screenName: string;
  currentMode: DataMode;
  newMode: DataMode;
}

export const ScreenSettingsPage: React.FC = () => {
  const { getAllScreenModes, setScreenMode, resetAllScreenModes } = useDataSource();
  const [confirmDialog, setConfirmDialog] = useState<ConfirmationDialog>({
    isOpen: false,
    screenId: '',
    screenName: '',
    currentMode: 'demo',
    newMode: 'demo',
  });
  const [resetDialog, setResetDialog] = useState<{
    isOpen: boolean;
    mode: DataMode;
  }>({
    isOpen: false,
    mode: 'demo',
  });

  const allScreens = getAllScreenModes();

  // Group screens by category
  const groupedScreens = allScreens.reduce((acc, screen) => {
    const category = screen.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(screen);
    return acc;
  }, {} as Record<string, ScreenDataMode[]>);

  // Calculate statistics
  const stats = {
    total: allScreens.length,
    demo: allScreens.filter(s => s.mode === 'demo').length,
    live: allScreens.filter(s => s.mode === 'live').length,
  };

  const handleToggleClick = (screen: ScreenDataMode) => {
    const newMode: DataMode = screen.mode === 'demo' ? 'live' : 'demo';
    setConfirmDialog({
      isOpen: true,
      screenId: screen.screenId,
      screenName: screen.screenName,
      currentMode: screen.mode,
      newMode,
    });
  };

  const handleConfirmToggle = () => {
    setScreenMode(confirmDialog.screenId, confirmDialog.newMode);
    
    toast.success('Data mode updated', {
      description: `${confirmDialog.screenName} is now using ${confirmDialog.newMode === 'demo' ? 'Demo' : 'Live'} data`,
    });

    setConfirmDialog({
      isOpen: false,
      screenId: '',
      screenName: '',
      currentMode: 'demo',
      newMode: 'demo',
    });
  };

  const handleResetAll = (mode: DataMode) => {
    setResetDialog({
      isOpen: true,
      mode,
    });
  };

  const handleConfirmReset = () => {
    resetAllScreenModes(resetDialog.mode);
    
    toast.success('All screens reset', {
      description: `All screens are now using ${resetDialog.mode === 'demo' ? 'Demo' : 'Live'} data`,
    });

    setResetDialog({
      isOpen: false,
      mode: 'demo',
    });
  };

  const getCategoryIcon = (category: string) => {
    // Icons removed - using text labels only
    return '';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Screen Data Modes"
        subtitle="Configure data sources for each screen independently. Enable phased rollout by setting different screens to use Live or Demo data."
        size={36}
        showActions={false}
        showBreadcrumb={false}
        icon={<Monitor />}
      />

      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="space-y-2 text-sm">
              <p className="font-medium text-blue-900 dark:text-blue-100">
                Per-Screen Configuration
              </p>
              <p className="text-blue-700 dark:text-blue-300">
                Each screen can operate independently with its own data source. This allows you to:
              </p>
              <ul className="list-disc list-inside space-y-1 text-blue-700 dark:text-blue-300 ml-2">
                <li>Test Live data on specific screens while keeping others in Demo mode</li>
                <li>Gradually roll out Live data integration screen by screen</li>
                <li>Maintain Demo data for training and demonstrations on selected screens</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Screens
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Monitor className="h-5 w-5 text-muted-foreground" />
              <span className="text-2xl font-bold">{stats.total}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Demo Mode
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <TestTube2 className="h-5 w-5 text-amber-600" />
              <span className="text-2xl font-bold">{stats.demo}</span>
              <span className="text-sm text-muted-foreground">
                ({Math.round((stats.demo / stats.total) * 100)}%)
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Live Mode
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5 text-green-600" />
              <span className="text-2xl font-bold">{stats.live}</span>
              <span className="text-sm text-muted-foreground">
                ({Math.round((stats.live / stats.total) * 100)}%)
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Apply data mode settings to all screens at once
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => handleResetAll('demo')}
              className="gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Set All to Demo
            </Button>
            <Button
              variant="outline"
              onClick={() => handleResetAll('live')}
              className="gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              Set All to Live
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Screen List by Category */}
      <div className="space-y-6">
        {Object.entries(groupedScreens).map(([category, screens]) => (
          <Card key={category}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span>{category}</span>
                <Badge variant="secondary" className="ml-2">
                  {screens.length} {screens.length === 1 ? 'screen' : 'screens'}
                </Badge>
              </CardTitle>
              <CardDescription>
                Configure data modes for {category.toLowerCase()} screens
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {screens.map((screen) => (
                  <div
                    key={screen.screenId}
                    className={cn(
                      'flex items-center justify-between p-4 rounded-lg border transition-colors',
                      screen.mode === 'live'
                        ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800'
                        : 'bg-amber-50 border-amber-200 dark:bg-amber-950 dark:border-amber-800'
                    )}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{screen.screenName}</h4>
                          {screen.mode === 'live' ? (
                            <Badge className="bg-green-600 hover:bg-green-700 gap-1">
                              <Database className="h-3 w-3" />
                              Live Data
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-600 hover:bg-amber-700 gap-1">
                              <TestTube2 className="h-3 w-3" />
                              Demo Data
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {screen.mode === 'live'
                            ? 'Connected to production database'
                            : 'Using sample data for testing'}
                        </p>
                      </div>

                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 text-sm">
                          <span className={cn(
                            'font-medium',
                            screen.mode === 'demo' ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'
                          )}>
                            Demo
                          </span>
                          <Switch
                            checked={screen.mode === 'live'}
                            onCheckedChange={() => handleToggleClick(screen)}
                            className={cn(
                              'data-[state=checked]:bg-green-600',
                              'data-[state=unchecked]:bg-amber-600'
                            )}
                          />
                          <span className={cn(
                            'font-medium',
                            screen.mode === 'live' ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground'
                          )}>
                            Live
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Confirmation Dialog for Individual Screen */}
      <AlertDialog open={confirmDialog.isOpen} onOpenChange={(open) => 
        setConfirmDialog(prev => ({ ...prev, isOpen: open }))
      }>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {confirmDialog.newMode === 'live' ? (
                <AlertCircle className="h-5 w-5 text-amber-600" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-blue-600" />
              )}
              Switch to {confirmDialog.newMode === 'live' ? 'Live' : 'Demo'} Data?
            </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                          <div className="space-y-3 text-sm text-muted-foreground">
                            <p>
                              You are about to change the data mode for <strong>{confirmDialog.screenName}</strong>:
                            </p>
                
                            <div className="bg-muted p-3 rounded-lg space-y-2">
                              <div className="flex items-center justify-between text-sm">
                                <span>Current Mode:</span>
                                <Badge variant={confirmDialog.currentMode === 'live' ? 'default' : 'secondary'}>
                                  {confirmDialog.currentMode === 'live' ? 'Live' : 'Demo'}
                                </Badge>
                              </div>
                              <div className="flex items-center justify-between text-sm">
                                <span>New Mode:</span>
                                <Badge variant={confirmDialog.newMode === 'live' ? 'default' : 'secondary'}>
                                  {confirmDialog.newMode === 'live' ? 'Live' : 'Demo'}
                                </Badge>
                              </div>
                            </div>

                            {confirmDialog.newMode === 'live' ? (
                              <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 p-3 rounded-lg">
                                <p className="text-sm text-amber-900 dark:text-amber-100">
                                  <strong>Important:</strong> This screen will connect to your production database. 
                                  Ensure your backend API is properly configured and tested.
                                </p>
                              </div>
                            ) : (
                              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 p-3 rounded-lg">
                                <p className="text-sm text-blue-900 dark:text-blue-100">
                                  This screen will use sample data for testing and demonstrations.
                                </p>
                              </div>
                            )}
                          </div>
                        </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmToggle}>
              Confirm Change
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmation Dialog for Reset All */}
      <AlertDialog open={resetDialog.isOpen} onOpenChange={(open) => 
        setResetDialog(prev => ({ ...prev, isOpen: open }))
      }>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              Reset All Screens?
            </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                          <div className="space-y-3 text-sm text-muted-foreground">
                            <p>
                              You are about to set <strong>all {stats.total} screens</strong> to{' '}
                              <strong>{resetDialog.mode === 'live' ? 'Live' : 'Demo'}</strong> mode.
                            </p>

                            {resetDialog.mode === 'live' ? (
                              <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 p-3 rounded-lg">
                                <p className="text-sm text-amber-900 dark:text-amber-100">
                                  <strong>Warning:</strong> All screens will connect to your production database. 
                                  This action will affect the entire application.
                                </p>
                              </div>
                            ) : (
                              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 p-3 rounded-lg">
                                <p className="text-sm text-blue-900 dark:text-blue-100">
                                  All screens will use sample data for testing and demonstrations.
                                </p>
                              </div>
                            )}

                            <p className="text-sm">
                              This will override all individual screen settings. You can change them back individually later.
                            </p>
                          </div>
                        </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmReset}>
              Reset All Screens
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
